import React, { FC, useCallback, useContext, useEffect, useState } from 'react';

import { useMutation, useApolloClient } from '@apollo/client';
import { DndContext, MouseSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext } from '@dnd-kit/sortable';
import { Button, Input, Modal, Separator } from '@smartshell/ui-kit';

import { FeaturesContext } from 'features';
import { LocalizationContext } from 'localization';
import {
  CREATE_SHORTCUT_GROUPS,
  CreateShortcutGroupRequest,
  CreateShortcutGroupResponse,
  DELETE_SHORTCUT_GROUPS,
  DeleteShortcutGroupRequest,
  DeleteShortcutGroupResponse,
  IShortcutGroup,
  IUpdateSortRequest,
  SHORTCUT_GROUPS,
  UPDATE_SHORTCUT_GROUPS,
  UPDATE_SHORTCUT_GROUPS_SORT,
  UpdateShortcutGroupRequest,
  UpdateShortcutGroupResponse,
} from 'schemas';
import notification from 'services/notificationService';
import { ESyncScope } from 'types';

import { AddButton } from './components/AddButton';
import GroupItem from './components/GroupItem';
import { SaveButton } from './components/SaveButton';
import s from './GroupModal.module.scss';
import { GroupModalProps, IDeletedGroup } from './interfaces';

const EditGroupModal: FC<GroupModalProps> = ({
  closeModal,
  appType,
  groups,
  disabledGroup,
}) => {
  const { t } = useContext(LocalizationContext);
  const { shortcuts_sync_enabled } = useContext(FeaturesContext);

  const [sortedGroups, setSortedGroups] = useState(groups);
  const [newGroupName, setNewGroupName] = useState<string>('');
  const [updatedTitles, setUpdatedTitles] = useState<Record<number, string>>(
    {}
  );

  const [deletedGroups, setDeletedGroups] = useState<IDeletedGroup[]>([]);
  const client = useApolloClient();
  const [validationErrors, setValidationErrors] = useState<
    Record<number, string | null>
  >({});

  const [createShortcutGroup, { loading: createLoading }] = useMutation<
    CreateShortcutGroupResponse,
    CreateShortcutGroupRequest
  >(CREATE_SHORTCUT_GROUPS, {
    refetchQueries: [SHORTCUT_GROUPS],
    awaitRefetchQueries: true,
    onCompleted: () => {
      notification.info({
        t,
        type: 'success',
        title: t('group.created'),
        dismiss: 5,
      });
    },
    onError: (error) => {
      notification.info({
        t,
        type: 'error',
        message: error?.message,
        dismiss: 5,
      });
    },
  });

  const [updateShortcutGroup, { loading: updateLoading }] = useMutation<
    UpdateShortcutGroupResponse,
    UpdateShortcutGroupRequest
  >(UPDATE_SHORTCUT_GROUPS, {
    onCompleted: () => {
      notification.info({
        t,
        type: 'success',
        title: t('group.modal.notification.update'),
        dismiss: 5,
      });
    },
  });

  const [deleteShortcutGroup, { loading: deleteLoading }] = useMutation<
    DeleteShortcutGroupResponse,
    DeleteShortcutGroupRequest
  >(DELETE_SHORTCUT_GROUPS, {
    onCompleted: () => {
      notification.info({
        t,
        type: 'success',
        title: t('group.modal.notification.delete'),
        dismiss: 5,
      });
    },
    onError: (error) => {
      notification.info({
        t,
        type: 'error',
        title: t('errors.error'),
        message: error?.message,
      });
      throw error;
    },
  });

  const [updateShortcutGroupSort, { loading }] = useMutation<
    boolean,
    IUpdateSortRequest
  >(UPDATE_SHORTCUT_GROUPS_SORT, {
    refetchQueries: [SHORTCUT_GROUPS],
    awaitRefetchQueries: true,
  });

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 10,
    },
  });

  const sensors = useSensors(mouseSensor);

  const onDragEnd = useCallback(
    ({ over, active }) => {
      if (over && active && active.data.current?.type === 'GroupItem') {
        const overId = over.id;
        const activeId = active.id;
        if (overId === activeId) return;

        setSortedGroups((prev) => {
          const activeIndex = prev.findIndex(({ id }) => id === activeId);
          const overIndex = prev.findIndex(({ id }) => id === overId);

          if (activeIndex === -1 || overIndex === -1) return prev;

          const newSorted = arrayMove(prev, activeIndex, overIndex);

          const changedGroups: IShortcutGroup[] = [];

          newSorted.forEach((group, index) => {
            if (group.sort !== newSorted.length - index) {
              changedGroups.push({
                ...group,
                sort: newSorted.length - index,
              });
            }
          });

          if (changedGroups.length) {
            updateShortcutGroupSort({
              variables: {
                input: changedGroups.map(({ id, sort }) => ({
                  id,
                  sort,
                })),
              },
            });
          }

          return newSorted;
        });
      }
    },
    [updateShortcutGroupSort]
  );

  const handleGroupChange = useCallback(
    (id: number, title: string) => {
      setUpdatedTitles((prev) => {
        const originalTitle = groups.find((g) => g?.id === id)?.title;
        if (title === originalTitle) {
          const { [id]: _, ...rest } = prev;
          return rest;
        }
        return { ...prev, [id]: title };
      });
    },
    [groups]
  );

  const onSave = async (sync: ESyncScope | undefined): Promise<void> => {
    const originalGroups = [...sortedGroups];
    const originalDeletedGroups = [...deletedGroups];
    const originalUpdatedTitles = { ...updatedTitles };

    try {
      for (const { group } of deletedGroups) {
        try {
          await deleteShortcutGroup({
            variables: {
              id: group?.id,
              sync,
            },
          });
        } catch (e) {
          setSortedGroups((prev) => {
            const deletedIndex = originalDeletedGroups.find(
              (d) => d.group.id === group.id
            )?.deletedIndex;

            if (deletedIndex !== undefined) {
              const copy = [...prev];
              copy.splice(deletedIndex, 0, group);
              return copy;
            }
            return prev;
          });
        }
      }

      const changedGroups = groups.filter((group) => {
        const updatedTitle = updatedTitles[group?.id];
        return updatedTitle && updatedTitle !== group.title;
      });

      await Promise.all(
        changedGroups.map((group) => {
          const updatedTitle = updatedTitles[group?.id];
          return updateShortcutGroup({
            variables: {
              updateShortcutGroupId: group?.id,
              input: {
                title: updatedTitle,
                groupType: group.groupType,
                sort: group.sort,
              },
              sync,
            },
          });
        })
      );

      await client.refetchQueries({
        include: [SHORTCUT_GROUPS],
      });

      setDeletedGroups([]);
      setUpdatedTitles({});
    } catch (error: unknown) {
      setSortedGroups(originalGroups);
      setDeletedGroups(originalDeletedGroups);
      setUpdatedTitles(originalUpdatedTitles);

      await client.refetchQueries({
        include: [SHORTCUT_GROUPS],
      });

      if (error instanceof Error) {
        notification.info({
          t,
          type: 'error',
          title: error?.message,
          dismiss: 5,
        });
      } else {
        notification.info({
          t,
          type: 'error',
          title: t('errors.common'),
          dismiss: 5,
        });
      }
    }
  };

  useEffect(() => {
    setSortedGroups(groups);
  }, [groups]);

  const handleValidationError = useCallback(
    (groupId: number, error: string | null) => {
      setValidationErrors((prev) => ({
        ...prev,
        [groupId]: error,
      }));
    },
    []
  );

  return (
    <Modal onClose={closeModal} className={s.container}>
      <h2 className={s.title}>
        {appType === 'GAME'
          ? t('group.modal.title_2')
          : t('group.modal.title_1')}
      </h2>
      <span className={s.info}>{t('group.modal.description')}</span>

      <Input
        value={newGroupName}
        onChange={(value) => {
          setNewGroupName(value);
        }}
        title={t('group.modal.input.title')}
        className={s.input_create}
      />

      {shortcuts_sync_enabled ? (
        <AddButton
          onAdd={(sync) => {
            createShortcutGroup({
              variables: {
                input: {
                  groupType: appType,
                  sort: groups[0]?.sort + 1 || 1,
                  title: newGroupName,
                },
                sync,
              },
            });
          }}
          loading={createLoading}
          disabled={newGroupName === ''}
          setNewGroupName={setNewGroupName}
        />
      ) : (
        <Button
          variant="secondary"
          onClick={() => {
            return createShortcutGroup({
              variables: {
                input: {
                  groupType: appType,
                  sort: groups[0]?.sort + 1 || 1,
                  title: newGroupName,
                },
              },
            });
          }}
          fullWidth
          disabled={newGroupName === ''}
          className={s.add_button}
          isLoading={createLoading}
        >
          {t('group.modal.button.add')}
        </Button>
      )}
      <Separator className={s.separator} />

      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <SortableContext
          items={sortedGroups.map(({ id }) => id)}
          disabled={loading || sortedGroups.length < 2}
        >
          {sortedGroups.length > 0 ? (
            <div className={s.groups}>
              {sortedGroups.map((group) => (
                <GroupItem
                  group={group}
                  key={group?.id}
                  disabled={disabledGroup.has(group?.id)}
                  noDrag={sortedGroups.length < 2}
                  onChange={handleGroupChange}
                  updatedTitle={updatedTitles[group?.id]}
                  onDelete={(group) => {
                    setSortedGroups((prev) =>
                      prev.filter((g) => g?.id !== group?.id)
                    );
                    const deletedIndex = sortedGroups.findIndex(
                      (g) => g?.id === group?.id
                    );
                    setDeletedGroups((prev) => [
                      ...prev,
                      { group, deletedIndex },
                    ]);
                  }}
                  onValidationError={handleValidationError}
                />
              ))}
            </div>
          ) : (
            <div className={s.empty}>{t('group.modal.empty')}</div>
          )}
        </SortableContext>
      </DndContext>
      <Separator className={s.separator_2} />

      {shortcuts_sync_enabled ? (
        <SaveButton
          onSave={onSave}
          disabled={
            (Object.keys(updatedTitles).length === 0 &&
              deletedGroups.length === 0) ||
            Object.values(validationErrors).some(Boolean)
          }
          loading={deleteLoading || updateLoading}
        />
      ) : (
        <Button
          disabled={
            (Object.keys(updatedTitles).length === 0 &&
              deletedGroups.length === 0) ||
            Object.values(validationErrors).some(Boolean)
          }
          fullWidth
          className={s.save_button}
          onClick={() => onSave(undefined)}
        >
          {t('group.modal.save.button')}
        </Button>
      )}

      <Button fullWidth onClick={closeModal} variant="secondary">
        {t('cancel')}
      </Button>
    </Modal>
  );
};

export default EditGroupModal;
